/**
 * AI Recruit - Audit Trail Modal Component
 * Modal for viewing assignment and status change history with pagination
 */

import React, { useState, useMemo } from 'react'
import { XMarkIcon, ClipboardDocumentListIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { useAuditHistory } from '../../hooks/useAssignment'
import { useCandidateStages } from '../../hooks/useCandidateStages'
import LoadingSpinner from '../common/LoadingSpinner'

interface AuditTrailModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'resume_upload' | 'job_match'
  entityId: string
  entityTitle: string
}

const AuditTrailModal: React.FC<AuditTrailModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityTitle,
}) => {
  const [page, setPage] = useState(1)

  // Fetch audit history
  const { data, isLoading } = useAuditHistory(entityType, entityId, page, isOpen)

  // Map status_code -> the real status name (same source the dropdown uses), so
  // the audit trail matches the dropdown exactly (e.g. "HR Discussion", not "Hr Discussion").
  const { flatStatuses } = useCandidateStages()
  const statusNameByCode = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of flatStatuses) m.set(s.statusCode, s.statusName)
    return m
  }, [flatStatuses])

  // Map status_code -> its owning stage name (e.g. "Resume Screening") --
  // the raw status_code alone ("Accepted") doesn't say WHICH stage's
  // Accepted/Rejected this is once several stages share that wording.
  const stageNameByCode = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of flatStatuses) m.set(s.statusCode, s.stageName)
    return m
  }, [flatStatuses])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatStatusLabel = (status: string) => {
    // Prefer the configured status name so audit matches the dropdown exactly.
    const name = statusNameByCode.get(status)
    if (name) return name
    // Fallback: prettify the raw code (for old/unknown codes not in the master list).
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Change History</h3>
            <p className="text-sm text-gray-500">{entityTitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : data?.audit_history.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentListIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No Changes Yet</p>
              <p className="text-sm text-gray-500 mt-2">
                No assignment or status changes have been recorded.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data?.audit_history.map((entry) => (
                <div 
                  key={entry.id} 
                  className={`p-3 border-l-4 rounded-r-lg ${
                    entry.change_type === 'status_change' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-blue-500 bg-blue-50'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {entry.change_type === 'status_change' ? (
                        <ArrowPathIcon className="w-4 h-4 text-green-600" />
                      ) : (
                        <ClipboardDocumentListIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* First line: Date and User */}
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">{formatDate(entry.changed_at)}</span>
                        <span className="mx-1">•</span>
                        <span>{entry.changed_by_user_name}</span>
                      </div>
                      
                      {/* Second line: Change details */}
                      {entry.change_type === 'status_change' ? (
                        <>
                          {/* Which stage this status belongs to -- "Accepted"/
                              "Rejected" alone doesn't say whether this is
                              Resume Screening, Voice Screening, etc. */}
                          {stageNameByCode.get(entry.new_status || '') && (
                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-green-700">
                              {stageNameByCode.get(entry.new_status || '')}
                            </div>
                          )}
                          <div className="mt-0.5 text-sm">
                            <span className="text-gray-600">Status: </span>
                            {entry.old_status ? (
                              <>
                                <span className="font-medium text-gray-900">
                                  {formatStatusLabel(entry.old_status)}
                                </span>
                                <span className="mx-1 text-gray-400">→</span>
                                <span className="font-medium text-gray-900">
                                  {formatStatusLabel(entry.new_status || '')}
                                </span>
                              </>
                            ) : (
                              /* Initial status (no previous) — show just the new status */
                              <span className="font-medium text-gray-900">
                                {formatStatusLabel(entry.new_status || '')}
                              </span>
                            )}
                          </div>
                          {/* Reason only for real transitions; the initial "Submitted" has none */}
                          {entry.old_status && entry.change_reason && (
                            <div className="mt-1 text-sm text-gray-600">
                              <span className="font-medium">Reason:</span> {entry.change_reason}
                            </div>
                          )}
                        </>
                      ) : entry.change_type === 'resume_download' ? (
                        <>
                          <div className="mt-1 text-sm">
                            <span className="text-gray-600">Action: </span>
                            <span className="font-medium text-gray-900">Resume Downloaded</span>
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            <span className="font-medium">Candidate:</span> {entityTitle}
                          </div>
                          {entry.change_reason && (
                            <div className="mt-1 text-sm text-gray-600">
                              <span className="font-medium">Job:</span> {entry.change_reason.replace('Downloaded from job ', '')}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="mt-1 text-sm">
                            <span className="text-gray-600">Assignment: </span>
                            <span className="font-medium text-gray-900">
                              {entry.old_assigned_to_user_name || 'Unassigned'}
                            </span>
                            <span className="mx-1 text-gray-400">→</span>
                            <span className="font-medium text-gray-900">
                              {entry.new_assigned_to_user_name || 'Unassigned'}
                            </span>
                          </div>
                          {entry.change_reason && (
                            <div className="mt-1 text-sm text-gray-600">
                              <span className="font-medium">Reason:</span> {entry.change_reason}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="mt-4 flex items-center justify-center space-x-4 border-t pt-4">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={!data.has_previous}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-600">
              {data.page} of {data.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data.has_next}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        )}

        {/* Close button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default AuditTrailModal
